// Check if we're in a browser environment
const isBrowser = typeof window !== "undefined";

// Simple logger interface
interface SimpleLogger {
  error: (message: string, meta?: any) => void;
  warn: (message: string, meta?: any) => void;
  info: (message: string, meta?: any) => void;
  http: (message: string, meta?: any) => void;
  debug: (message: string, meta?: any) => void;
  log: (level: string, message: string, meta?: any) => void;
}

// Log entry interface for JSON structure
interface LogEntry {
  timestamp: string;
  level: string;
  message: string;
  meta?: any;
  service?: string;
  environment?: string;
}

// Format message as JSON for Dozzle compatibility
const formatJsonMessage = (
  level: string,
  message: string,
  meta?: any
): string => {
  const logEntry: LogEntry = {
    timestamp: new Date().toISOString(),
    level: level.toUpperCase(),
    message,
    service: "candidate-app",
    environment: process.env.NODE_ENV || "development",
  };

  if (meta) {
    logEntry.meta = meta;
  }

  return JSON.stringify(logEntry);
};

// Create server-side logger (JSON-based for Dozzle)
const createServerLogger = (): SimpleLogger => {
  return {
    error: (message: string, meta?: any) => {
      console.error(formatJsonMessage("error", message, meta));
    },
    warn: (message: string, meta?: any) => {
      console.warn(formatJsonMessage("warn", message, meta));
    },
    info: (message: string, meta?: any) => {
      console.info(formatJsonMessage("info", message, meta));
    },
    http: (message: string, meta?: any) => {
      console.log(formatJsonMessage("http", message, meta));
    },
    debug: (message: string, meta?: any) => {
      console.debug(formatJsonMessage("debug", message, meta));
    },
    log: (level: string, message: string, meta?: any) => {
      const jsonMessage = formatJsonMessage(level, message, meta);
      switch (level) {
        case "error":
          console.error(jsonMessage);
          break;
        case "warn":
          console.warn(jsonMessage);
          break;
        case "info":
          console.info(jsonMessage);
          break;
        case "debug":
          console.debug(jsonMessage);
          break;
        default:
          console.log(jsonMessage);
      }
    },
  };
};

// Create a no-op logger for browser environments
const noOpLogger: SimpleLogger = {
  error: () => {},
  warn: () => {},
  info: () => {},
  http: () => {},
  debug: () => {},
  log: () => {},
};

// Create the appropriate logger based on environment
const logger: SimpleLogger = isBrowser ? noOpLogger : createServerLogger();

// Create a stream object for Morgan (if you want to use it with Express)
const stream = {
  write: (message: string) => {
    if (!isBrowser) {
      logger.http(message.trim());
    }
  },
};

// Export the logger and stream
export { logger, stream };

// Export individual log methods for convenience
export const logError = (message: string, meta?: any) => {
  if (!isBrowser) {
    logger.error(message, meta);
  }
};

export const logWarn = (message: string, meta?: any) => {
  if (!isBrowser) {
    logger.warn(message, meta);
  }
};

export const logInfo = (message: string, meta?: any) => {
  if (!isBrowser) {
    logger.info(message, meta);
  }
};

export const logHttp = (message: string, meta?: any) => {
  if (!isBrowser) {
    logger.http(message, meta);
  }
};

export const logDebug = (message: string, meta?: any) => {
  if (!isBrowser) {
    logger.debug(message, meta);
  }
};

// Default export
export default logger;
